import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import SideDrawer from "../../components/modals/SideDrawer/SideDrawer";
import Skeleton from "../../components/Skeleton/Skeleton";
import { deleteObservation, getObservations } from "../../services/observationService";
import {
  FINALIZED_STATUSES,
  OBSERVATION_STATUS,
  OBSERVATION_TYPE,
} from "../../utils/constants/observations";
import { fullName } from "../../utils/helpers/userDisplay";
import { formatApiError } from "../../utils/helpers/apiErrors";
import { formatShortDate, parseLocalISODate } from "../../utils/helpers/dateDisplay";
import { FOCUS_RING, META_LABEL, PRIMARY_BTN } from "./coverageStyles";

const FINALIZED_PARAM = [...FINALIZED_STATUSES].join(",");
const DRAWER_PAGE_SIZE = 50;

const OTHER_RELATION = { conducted: "received", received: "conducted" };

// `observed_at` is a DATETIME filter, so a bare "2026-10-31" binds to that day's
// midnight and drops every visit run during the closing day of the period. The
// window is spelled out to the last second at both ends.
const startOfDay = (isoDate) => (isoDate ? `${isoDate}T00:00:00` : undefined);
const endOfDay = (isoDate) => (isoDate ? `${isoDate}T23:59:59` : undefined);

const isOverdue = (observation) => {
  const due = parseLocalISODate(observation.scheduled_date);
  if (!due) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return due < today;
};

function TrashIcon() {
  return (
    <svg aria-hidden="true" className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  );
}

// Asked in the row, not in a dialog: the drawer is already a dialog, and the
// question is about this one row.
function DeleteControl({ observation, onDeleted, t }) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await deleteObservation({ uuid: observation.uuid });
      toast.success(t("coverage.drawer.deleted"));
      onDeleted(observation.uuid);
    } catch (err) {
      toast.error(formatApiError(err, t("coverage.drawer.deleteFailed")));
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        aria-label={t("coverage.drawer.delete")}
        title={t("coverage.drawer.delete")}
        className={`shrink-0 rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-rose-50 hover:text-rose-600 ${FOCUS_RING}`}
      >
        <TrashIcon />
      </button>
    );
  }

  return (
    <div className="flex basis-full items-center justify-end gap-2 pt-1">
      <span className="mr-auto text-xs font-bold text-rose-700">
        {t("coverage.drawer.deleteConfirm")}
      </span>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        disabled={busy}
        className={`rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-40 ${FOCUS_RING}`}
      >
        {t("common.cancel")}
      </button>
      <button
        type="button"
        onClick={remove}
        disabled={busy}
        className={`rounded-lg bg-rose-600 px-3 py-1.5 text-[11px] font-bold text-white hover:bg-rose-700 disabled:opacity-60 ${FOCUS_RING}`}
      >
        {t("coverage.drawer.delete")}
      </button>
    </div>
  );
}

function VisitRow({ observation, relation, onDeleted, t }) {
  // The drawer is about one person, so each row names the OTHER party: their
  // own name repeated down the list carries nothing.
  const counterpart =
    relation === "conducted" ? observation.observed_teacher : observation.main_observer;
  const peer = observation.type === OBSERVATION_TYPE.PEER;
  const context = [observation.group?.name, observation.domain_subject?.name]
    .filter(Boolean)
    .join(" · ");
  const late = observation.status === OBSERVATION_STATUS.SCHEDULED && isOverdue(observation);
  const date = observation.observed_at || observation.scheduled_date;

  return (
    <li className="flex flex-wrap items-start gap-3 py-3">
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-bold text-slate-900">
          {counterpart ? fullName(counterpart) : t("coverage.queue.noObserver")}
        </p>
        <p className="mt-0.5 truncate text-xs font-medium text-slate-500">
          {[
            formatShortDate(date),
            observation.period ? t("upcoming.period", { period: observation.period }) : "",
            context,
          ]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <div className="flex shrink-0 flex-col items-end gap-1">
        <span
          className={`rounded-md px-2 py-0.5 text-[10px] font-bold leading-none ${
            peer ? "bg-indigo-50 text-indigo-600" : "bg-slate-100 text-slate-600"
          }`}
        >
          {peer ? t("upcoming.peerVisit") : t("upcoming.officialObservation")}
        </span>
        {late && (
          <span className="rounded-md bg-amber-50 px-2 py-0.5 text-[10px] font-bold leading-none text-amber-700">
            {t("coverage.state.overdue")}
          </span>
        )}
      </div>
      {onDeleted && <DeleteControl observation={observation} onDeleted={onDeleted} t={t} />}
    </li>
  );
}

function Section({ title, hint, status, rows, relation, emptyText, onDeleted, t }) {
  return (
    <section className="space-y-2">
      <div>
        <h4 className={META_LABEL}>{title}</h4>
        {hint && <p className="mt-0.5 text-[11px] font-medium text-slate-400">{hint}</p>}
      </div>
      {status === "loading" ? (
        <div className="space-y-2 pt-1">
          <Skeleton className="h-10 w-full rounded-xl" />
          <Skeleton className="h-10 w-full rounded-xl" />
        </div>
      ) : status === "error" ? (
        <p className="py-4 text-sm font-semibold text-slate-400">{t("dashboard.panelError")}</p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-sm font-medium text-slate-400">{emptyText}</p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {rows.map((observation) => (
            <VisitRow
              key={observation.uuid}
              observation={observation}
              relation={relation}
              onDeleted={onDeleted}
              t={t}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

// The two role groups, as a switch. It replaces the page-level tab that used to
// decide this for every person at once: which visits a row's drill-in shows is a
// property of the PERSON you clicked, not of a mode the whole screen is in.
// Rendered only when both groups exist, because a one-option switch is a label
// that looks clickable.
function RelationSwitch({ value, onChange, t }) {
  return (
    <div
      role="group"
      aria-label={t("coverage.drawer.relationLabel")}
      className="flex gap-1 rounded-xl bg-slate-100 p-1"
    >
      {["conducted", "received"].map((key) => {
        const active = key === value;
        return (
          <button
            key={key}
            type="button"
            aria-pressed={active}
            onClick={() => onChange(key)}
            className={`flex-1 rounded-lg px-3 py-1.5 text-[11px] font-bold uppercase tracking-wide transition ${FOCUS_RING} ${
              active ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"
            }`}
          >
            {key === "conducted" ? t("coverage.group.observer") : t("coverage.group.teacher")}
          </button>
        );
      })}
    </div>
  );
}

// One person's visits: what they did inside the period, and what is still
// booked in their name. There is no percentage here and no route into the
// teacher overview — that modal is a full score surface, and a coverage row is
// a count of visits, not a verdict on anybody.
//
// `relation` is where the drawer OPENS, not what it is stuck on: the caller
// passes the column that was clicked, and the switch above reaches the other
// group. The caller keys this component on that pair, so every open starts on
// the column the viewer actually pointed at.
export default function VisitsDrawer({
  row,
  relation,
  organizationUuid,
  period,
  canSchedule,
  canDelete,
  onAssign,
  onDeleted,
  onClose,
}) {
  const { t } = useTranslation();
  const person = row?.person || {};
  // A null count is the backend saying this person holds no such role, so the
  // switch offers what they actually are — and an opening relation they cannot
  // hold falls to the one they can, rather than fetching a list that is empty
  // by definition and reading as "nothing scheduled".
  const canConduct = row?.conducted_total != null;
  const canBeObserved = row?.received_total != null;
  const wanted = relation === "conducted" ? "conducted" : "received";
  const holds = { conducted: canConduct, received: canBeObserved };
  const [active, setActive] = useState(holds[wanted] ? wanted : OTHER_RELATION[wanted]);

  const [done, setDone] = useState({ status: "loading", rows: [] });
  const [pipeline, setPipeline] = useState({ status: "loading", rows: [] });

  useEffect(() => {
    if (!person.uuid) return undefined;
    const controller = new AbortController();
    const { signal } = controller;
    const who =
      active === "conducted"
        ? { main_observer: person.uuid }
        : { observed_teacher__uuid: person.uuid };

    const load = (request, apply) =>
      request
        .then((data) => {
          if (signal.aborted) return;
          apply({ status: "ready", rows: data?.results || [] });
        })
        .catch((err) => {
          if (signal.aborted || err?.code === "ERR_CANCELED") return;
          apply({ status: "error", rows: [] });
        });

    setDone({ status: "loading", rows: [] });
    setPipeline({ status: "loading", rows: [] });

    load(
      getObservations({
        params: {
          organization: organizationUuid,
          ...who,
          status__in: FINALIZED_PARAM,
          observed_at__gte: startOfDay(period?.start),
          observed_at__lte: endOfDay(period?.end),
          ordering: "-observed_at",
          page_size: DRAWER_PAGE_SIZE,
        },
        signal,
      }),
      setDone,
    );

    // Everything still booked, past date or not: the overdue ones are the point,
    // but a queue shown without the visits that are simply upcoming reads as if
    // the person had nothing planned at all.
    load(
      getObservations({
        params: {
          organization: organizationUuid,
          ...who,
          status: OBSERVATION_STATUS.SCHEDULED,
          ordering: "scheduled_date",
          page_size: DRAWER_PAGE_SIZE,
        },
        signal,
      }),
      setPipeline,
    );

    return () => controller.abort();
  }, [person.uuid, active, organizationUuid, period?.start, period?.end]);

  // Only finished visits carry the control: a booking that is not happening is
  // cancelled (the overdue queue), not deleted.
  const removeDone = (uuid) => {
    setDone((prev) => ({ ...prev, rows: prev.rows.filter((o) => o.uuid !== uuid) }));
    onDeleted?.();
  };

  const rangeText =
    period?.start && period?.end
      ? `${formatShortDate(parseLocalISODate(period.start))} — ${formatShortDate(parseLocalISODate(period.end))}`
      : "";

  return (
    <SideDrawer
      title={fullName(person)}
      subtitle={
        active === "conducted"
          ? t("coverage.drawer.subConducted")
          : t("coverage.drawer.subReceived")
      }
      onClose={onClose}
      footer={
        canSchedule ? (
          <button
            type="button"
            onClick={() => onAssign(active)}
            className={`${PRIMARY_BTN} w-full justify-center`}
          >
            {t("schedule.scheduleObservation")}
          </button>
        ) : null
      }
    >
      <div className="space-y-7">
        {canConduct && canBeObserved && (
          <RelationSwitch value={active} onChange={setActive} t={t} />
        )}
        <Section
          title={t("coverage.drawer.inPeriod")}
          hint={rangeText}
          status={done.status}
          rows={done.rows}
          relation={active}
          emptyText={t("coverage.drawer.inPeriodEmpty")}
          onDeleted={canDelete ? removeDone : undefined}
          t={t}
        />
        <Section
          title={t("coverage.drawer.pipeline")}
          hint={t("coverage.drawer.pipelineHint")}
          status={pipeline.status}
          rows={pipeline.rows}
          relation={active}
          emptyText={t("coverage.drawer.pipelineEmpty")}
          t={t}
        />
        {/* Said out loud rather than implied by an absence: this drawer counts
            visits and will never show a score, however the person got here. */}
        <p className="rounded-2xl bg-slate-50 px-4 py-3 text-[11px] font-medium leading-relaxed text-slate-500">
          {t("coverage.drawer.noScoresNote")}
        </p>
      </div>
    </SideDrawer>
  );
}
